import type { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useWeekIndex } from "@/contexts/WeekIndexProvider/hook";
import { useWeekViewContext } from "@/contexts/WeekView/WeekViewContext";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useShownWeek } from "@/hooks/useShownWeek";
import { getVisibleWeekOffset, getWeekStart } from "@/utils/dateUtils";
import { DEFAULT_WEEK_START_DAY } from "@/utils/models";
import { toIsoDate } from "@/utils/scheduleWeeks";

const DAYS_PER_WEEK = 7;

export type GapsVisibleWeek = {
	/** True when weeks start on the user's chosen day rather than Sunday — the desktop reads `visibleWeekViewData` then. */
	isDynamicWeekStartEnabled: boolean;
	/** The first day of the visible week a calendar date falls in. */
	weekStartOf: (date: DateTime) => DateTime;
	/** The days of the visible week starting on `weekStart` that the schedule covers; none while its range is unknown. */
	daysOfWeek: (weekStart: DateTime) => DateTime[];
	/**
	 * The first day the home can show: the first visible week the schedule covers whole starts on it. With weeks starting
	 * after Sunday, the schedule's first days before the start day belong to a week that begins before the schedule.
	 */
	firstDay: DateTime | undefined;
	/** The schedule's last day; the last visible week can run past it. */
	lastDay: DateTime | undefined;
	/** `date` is between `firstDay` and `lastDay`, by calendar date; false while the schedule's range is unknown. */
	isShownDay: (date: DateTime) => boolean;
	/** `date`'s visible week is the one the staffing pages show (`useShownWeek`). */
	isWeekShown: (date: DateTime) => boolean;
	/** Makes `date`'s visible week the one the staffing pages show. */
	showWeekOf: (date: DateTime) => void;
};

/**
 * The weeks the gaps-first home pages through, drawn exactly as the desktop weekly view draws them. With
 * `StaffingDynamicWeekStart` on, a week starts on the user's `viewStartDay` and spills into the next Sunday week
 * (`WeekViewProvider.visibleWeekViewData`); off, it is the Sunday week. The weeks are dates, so with
 * `staffing_schedule_cache` on the home reaches every week of the schedule; off, the schedule's range is the old list's
 * loaded weeks, as before.
 *
 * Every comparison is by calendar date: the URL's day is a midnight in the browser's zone, the schedule's in the
 * department's.
 */
export const useGapsVisibleWeek = (): GapsVisibleWeek => {
	const { viewStartDay } = useWeekIndex();
	const { firstDate, lastDate } = useWeekViewContext();
	const { weekStart: shownWeekStart, goToWeek } = useShownWeek();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const offset = isDynamicWeekStartEnabled ? getVisibleWeekOffset(viewStartDay) : 0;

	const weekStartOf = useCallback((date: DateTime): DateTime => getWeekStart(date, offset), [offset]);

	const firstDay = useMemo<DateTime | undefined>(() => {
		if (firstDate === undefined) return undefined;
		const weekStart = getWeekStart(firstDate, offset);
		return toIsoDate(weekStart) < toIsoDate(firstDate) ? weekStart.plus({ weeks: 1 }) : weekStart;
	}, [firstDate, offset]);

	const isShownDay = useCallback(
		(date: DateTime): boolean =>
			firstDay !== undefined && lastDate !== undefined && toIsoDate(firstDay) <= toIsoDate(date) && toIsoDate(date) <= toIsoDate(lastDate),
		[firstDay, lastDate],
	);

	const daysOfWeek = useCallback(
		(weekStart: DateTime): DateTime[] => Array.from({ length: DAYS_PER_WEEK }, (_, column) => weekStart.plus({ days: column })).filter(isShownDay),
		[isShownDay],
	);

	// The staffing pages show Sunday weeks; a visible week starting after Sunday is drawn from the one it begins in.
	const isWeekShown = useCallback(
		(date: DateTime): boolean =>
			shownWeekStart !== undefined && toIsoDate(shownWeekStart) === toIsoDate(getWeekStart(weekStartOf(date), DEFAULT_WEEK_START_DAY)),
		[shownWeekStart, weekStartOf],
	);

	const showWeekOf = useCallback(
		(date: DateTime): void => {
			goToWeek(weekStartOf(date));
		},
		[goToWeek, weekStartOf],
	);

	return { isDynamicWeekStartEnabled, weekStartOf, daysOfWeek, firstDay, lastDay: lastDate, isShownDay, isWeekShown, showWeekOf };
};
